import type { SchemaTypes } from '@allesauto/graphql';
import { QueryModelsByMake } from '@allesauto/graphql';
import translate from '@allesauto/i18n';
import { checkSubGroups, formatPrice } from '@allesauto/utils/funcs';
import { isAuregoShop } from '@allesauto/utils/funcs.ts';
import { useQuery } from '@apollo/client';
import clsx from 'clsx';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useIntl } from 'react-intl';
import { WL_INTEGRATION_SLUG_AUREGO } from 'web/src/features/whitelabel/util/getWhiteLabelIntegrationSlugs';

import Button from '../button';
import CircularLoader, {
  CircularLoaderSize,
  CircularLoaderType,
} from '../circularLoader';
import LoaderOverlay from '../loaderOverlay';
import NextLink from '../nextLink';
import RadioButton from '../radioButton';
import SearchDropdown, { Option } from '../searchDropdown';
import Slider from '../slider';
import TextLink from '../textLink';
import { useWhiteLabel } from '../whiteLabel';

type Orientation = 'horizontal' | 'vertical';

type HandlerParams = {
  isDefaultPriceRange: boolean;
  priceRange: {
    key: string;
    min: number;
    max: number;
  };
  make?: string;
  model?: string;
  modelGroup?: string;
};

export interface VehicleSearchProps {
  facets: {
    make: {
      name: string;
      displayName: string;
    }[];
    price: {
      key: string;
      min: number;
      max: number;
    }[];
  };
  darkBg?: boolean;
  hasExtraLinks?: boolean;
  orientation?: Orientation;
  onChange?: (params: HandlerParams) => void;
  onSubmit: (params: HandlerParams) => Promise<void>;
  customButtonName?: string;
}

interface ModelsQuery {
  models: SchemaTypes.Model[];
}

const getSliderSteps = (price: number) => {
  if (price < 500) return 25;
  if (price < 1000) return 50;
  if (price < 5000) return 100;
  if (price < 30000) return 500;
  return 1000;
};

const roundedMinSliderRange = (min: number, key: string, max: number) => {
  // Set min slider range to rounded value.
  // Relevant for clean slider steps:
  // 50 > 75 > 100 > ... 1000 > 1050 > 1100  ... > 5000 > 5100 > 5200 etc.
  const CASH_INCREMENT = 1000;
  const FINANCING_INCREMENT = 50;
  let roundedMin = 0;

  if (key === 'CASH_RANGE') {
    if (max === 0) return roundedMin;

    roundedMin = CASH_INCREMENT;
    while (roundedMin < min) {
      roundedMin += CASH_INCREMENT;
    }
  }

  if (key === 'LOAN_RATE_RANGE' || key === 'LEASING_RATE_RANGE') {
    if (max === 0) return roundedMin;

    roundedMin = FINANCING_INCREMENT;
    while (roundedMin < min) {
      roundedMin += FINANCING_INCREMENT;
    }
  }

  return roundedMin;
};

const VehicleSearch = ({
  facets: { make: makes, price: priceRanges },
  darkBg = false,
  hasExtraLinks = true,
  orientation = 'vertical',
  onChange,
  onSubmit,
  customButtonName,
}: VehicleSearchProps) => {
  const whiteLabel = useWhiteLabel();
  const defaultPriceRange = priceRanges[0];

  const [priceKey, setPriceKey] = useState(defaultPriceRange.key);
  const [make, setMake] = useState<Option | null>(null);
  const [model, setModel] = useState<Option | null>(null);
  const [price, setPrice] = useState(defaultPriceRange.max);
  const [models, setModels] = useState<SchemaTypes.Model[]>([]);
  const [loading, setLoading] = useState<string | undefined>();

  //const router = useRouter();
  const intl = useIntl();
  const didMountRef = useRef(false);
  const hasDarkBg = whiteLabel ? false : darkBg; // always use light mode for WhiteLabel

  const {
    loading: modelsLoading,
    error: modelsError,
    data: modelsData,
  } = useQuery<ModelsQuery>(QueryModelsByMake, {
    variables: { makeName: make?.value },
    skip: !make,
  });

  useMemo(() => {
    setModels(modelsData?.models ?? []);
  }, [modelsData]);

  useEffect(() => {
    if (modelsLoading) {
      setModel(null);
      setModels([]);
    }
  }, [make, modelsLoading]);

  const priceRange = useMemo(
    () => priceRanges.find((p) => p.key === priceKey) || defaultPriceRange,
    [priceRanges, priceKey, defaultPriceRange]
  );

  const isDefaultPriceRange =
    priceKey === defaultPriceRange.key && price === defaultPriceRange.max;

  useEffect(() => {
    setPrice(priceRange.max);
  }, [priceRange]);

  const tooltipFormatter = useCallback(
    (value: number) => formatPrice(intl, value),
    [intl]
  );

  const selectedModel = model?.value.toString();

  const modelDropdownHasSubGroups = checkSubGroups(models, selectedModel);

  const handlerParams = useMemo<HandlerParams>(() => {
    const params = {
      isDefaultPriceRange,
      priceRange: {
        key: priceRange.key,
        min: priceRange.min,
        max: price,
      },
      make: make?.value.toString(),
      model: '',
      modelGroup: '',
    };

    if (modelDropdownHasSubGroups) {
      params.modelGroup = selectedModel ?? '';
    } else {
      params.model = selectedModel ?? '';
    }

    return params;
  }, [
    isDefaultPriceRange,
    make?.value,
    modelDropdownHasSubGroups,
    price,
    priceRange.key,
    priceRange.min,
    selectedModel,
  ]);

  useEffect(() => {
    if (didMountRef.current) {
      onChange?.(handlerParams);
    }

    didMountRef.current = true;
  }, [handlerParams, onChange]);

  const handleSubmit = useCallback(() => {
    setLoading('submitBtn');
    onSubmit(handlerParams).finally(() => {
      setLoading(undefined);
    });
  }, [handlerParams, onSubmit]);

  const linkHandler = useCallback(
    (value: string | undefined) => () => {
      setLoading(value);
    },
    []
  );

  const [sliderSteps, setSliderSteps] = useState(() => getSliderSteps(price));

  useEffect(() => {
    setSliderSteps(getSliderSteps(price));
  }, [price]);

  const dropdownOptions =
    (models.map((m) => {
      if (m.subGroups?.length) {
        const subGroupOptions = m.subGroups.map((subGroup) => ({
          value: String(subGroup?.name),
          label: String(subGroup?.displayName),
        })) as Option[];

        // Add (Alle) if subGroups exist
        subGroupOptions.unshift({
          value: String(m?.name),
          label: String(m?.displayName) + ' (Alle)',
        });

        return {
          label: (
            <label className="font-weight-bold text-[#a09c9c] text-[16px]">
              {String(m?.displayName)}
            </label>
          ),
          options: subGroupOptions,
        };
      } else {
        return {
          value: String(m?.name),
          label: String(m?.displayName),
        };
      }
    }) as Option[]) || [];

  return (
    <div
      className={orientation === 'vertical' ? undefined : 'grid grid-cols-24'}
    >
      {loading !== undefined && (
        <LoaderOverlay hasLoader={false} hasZeroOpacity />
      )}
      <div
        className={clsx(
          'flex flex-col',
          orientation === 'vertical'
            ? 'md:flex-row md:mb-25px mb-20px'
            : 'col-start-2 col-end-8 pt-20px pb-20px'
        )}
      >
        <div
          className={clsx(
            'mb-10px mr-0 w-full',
            orientation === 'vertical' && 'md:mb-0 md:pr-20px md:w-1/2'
          )}
        >
          <SearchDropdown
            isMulti={false}
            options={makes.map((m) => ({
              value: m.name,
              label: m.displayName,
            }))}
            placeholder={translate(
              intl,
              'ui.QUICK_ENTRY_WIDGET.VEHICLE_SEARCH_FORM.DROPDOWN_MAKE'
            )}
            value={make}
            onChange={setMake as any}
            context="vehicle-search-form"
            darkBg={hasDarkBg}
          />
        </div>

        <div
          className={clsx(
            'ml-0 w-full',
            orientation === 'vertical' && 'md:pl-20px md:w-1/2'
          )}
        >
          <SearchDropdown
            isMulti={false}
            disabled={modelsLoading || !!modelsError || !models.length}
            options={dropdownOptions}
            placeholder={translate(
              intl,
              'ui.QUICK_ENTRY_WIDGET.VEHICLE_SEARCH_FORM.DROPDOWN_MODEL'
            )}
            value={model}
            onChange={setModel as any}
            context="vehicle-search-form"
            darkBg={hasDarkBg}
            isLoading={modelsLoading}
          />
        </div>
      </div>

      <div
        className={
          orientation === 'vertical'
            ? undefined
            : 'col-start-10 col-end-16 pt-20px pb-20px'
        }
      >
        <div
          className={clsx(
            'flex justify-between mb-15px',
            orientation === 'vertical' && 'md:justify-start md:mb-20px'
          )}
        >
          <RadioButton
            name="paymentMethod"
            value={priceRanges[0].key}
            checked={priceKey === priceRanges[0].key}
            onChange={setPriceKey}
            label={translate(intl, 'ui.COMMON.PAYMENT_OPTION.CASH')}
            darkBg={hasDarkBg}
          />
          <RadioButton
            name="paymentMethod"
            value={priceRanges[1].key}
            checked={priceKey === priceRanges[1].key}
            onChange={setPriceKey}
            label={translate(intl, 'ui.COMMON.PAYMENT_OPTION.FINANCING')}
            className="ml-20px"
            darkBg={hasDarkBg}
          />
          <RadioButton
            name="paymentMethod"
            value={priceRanges[2].key}
            checked={priceKey === priceRanges[2].key}
            onChange={setPriceKey}
            label={translate(intl, 'ui.COMMON.PAYMENT_OPTION.LEASING')}
            className="ml-20px"
            darkBg={hasDarkBg}
          />
        </div>

        <div
          className={clsx(
            'relative w-[99%]',
            orientation === 'vertical' ? 'mb-[40px]' : 'mb-[10px]'
          )}
        >
          <Slider
            range={false}
            min={roundedMinSliderRange(
              priceRange.min,
              priceKey,
              priceRange.max
            )}
            max={priceRange.max}
            step={sliderSteps}
            value={[price]}
            onChange={setPrice as any}
            tooltipFormatter={tooltipFormatter as any}
            darkBg={hasDarkBg}
          />
        </div>
      </div>

      <div
        className={
          orientation === 'vertical' ? undefined : 'col-start-18 col-end-25'
        }
      >
        <Button
          onClick={handleSubmit}
          fullWidth
          darkBg={hasDarkBg}
          whitelabel={Boolean(whiteLabel)}
          customClassName={
            orientation === 'vertical'
              ? `${
                  whiteLabel?.merchantOrigin.includes(
                    WL_INTEGRATION_SLUG_AUREGO
                  )
                    ? 'aurego-color-blue'
                    : '!text-white'
                }`
              : 'h-[100%] rounded-none'
          }
        >
          {loading === 'submitBtn' ? (
            <CircularLoader
              type={
                isAuregoShop(whiteLabel)
                  ? CircularLoaderType.secondary
                  : CircularLoaderType.primary
              }
              customSize={CircularLoaderSize.sm}
            />
          ) : customButtonName ? (
            customButtonName
          ) : (
            translate(intl, 'ui.QUICK_ENTRY_WIDGET.VEHICLE_SEARCH_FORM.BUTTON')
          )}
        </Button>
      </div>

      {hasExtraLinks && (
        <div className="flex flex-col justify-center mt-20px md:flex-row md:mt-25px">
          <NextLink
            href="/pages/auto-an-haendler-verkaufen"
            passHref
            legacyBehavior
          >
            <TextLink
              text={translate(
                intl,
                'ui.QUICK_ENTRY_WIDGET.VEHICLE_SEARCH_FORM.LINK_SELL_CAR'
              )}
              customClassName="md:mr-20px"
              onClick={linkHandler('carSell')}
              isLoading={loading === 'carSell'}
            />
          </NextLink>
        </div>
      )}
    </div>
  );
};

export default VehicleSearch;
